/**
 * Modal generico del design system (primo consumer: editing voci CER).
 *
 * Pattern coerente con i modal hand-rolled esistenti (overlay scuro + pannello
 * bianco arrotondato, header con titolo e X, body scrollabile, footer azioni):
 * i modal esistenti NON vengono migrati qui in blocco — conversione progressiva
 * nei task che li toccano.
 *
 * Comportamento: chiusura su ESC (ignorata se un figlio ha già fatto
 * preventDefault, es. combobox con lista aperta) e su click dell'overlay;
 * focus portato sul pannello all'apertura (ESC funziona subito, il focus
 * resta nel contesto del modal come nei modal esistenti).
 */
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Classe max-width del pannello (default max-w-lg). */
  maxWidth?: string;
  testid?: string;
}

export default function Modal({
  title, subtitle, onClose, children, footer, maxWidth = 'max-w-lg', testid,
}: ModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus SOLO al mount: se stesse in un effect con dipendenze (es. onClose,
  // spesso ricreata dal padre a ogni render), ogni keystroke nei children
  // rieseguirebbe l'effect e il pannello ruberebbe il focus all'input.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative bg-white rounded-xl shadow-xl w-full ${maxWidth} mx-4 max-h-[90vh] flex flex-col outline-none`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-ink-dark">{title}</h2>
            {subtitle && <p className="text-xs text-ink-soft mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark" aria-label={t('common.azioni.chiudi')}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg flex-shrink-0 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
